import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { MessageSquare, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/support")({
  head: () => ({ meta: [{ title: "Support — Admin" }] }),
  component: AdminSupport,
});

function AdminSupport() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState<"open" | "closed" | "all">("open");
  const [reply, setReply] = useState("");

  const { data: tickets = [] } = useQuery({
    queryKey: ["admin-tickets", filter],
    queryFn: async () => {
      let q = supabase
        .from("tickets")
        .select("*")
        .order("updated_at", { ascending: false });
      if (filter !== "all") q = q.eq("status", filter);
      const { data, error } = await q;
      if (error) throw error;
      const rows = data ?? [];
      const userIds = Array.from(new Set(rows.map((r) => r.user_id)));
      if (userIds.length === 0) return rows.map((r) => ({ ...r, profile: null as { email: string | null; full_name: string | null } | null }));
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, email, full_name")
        .in("id", userIds);
      const byId = new Map((profiles ?? []).map((p) => [p.id, p]));
      return rows.map((r) => ({ ...r, profile: byId.get(r.user_id) ?? null }));
    },
  });

  const { data: messages = [] } = useQuery({
    enabled: !!selected,
    queryKey: ["admin-ticket-messages", selected],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ticket_messages")
        .select("*")
        .eq("ticket_id", selected!)
        .order("created_at");
      if (error) throw error;
      return data ?? [];
    },
  });

  const send = useMutation({
    mutationFn: async (input: { body: string; close: boolean }) => {
      if (!selected) throw new Error("No ticket selected");
      const { error } = await supabase.rpc("admin_reply_ticket", {
        _ticket_id: selected,
        _body: input.body,
        _close: input.close,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setReply("");
      toast.success("Reply sent");
      qc.invalidateQueries({ queryKey: ["admin-ticket-messages", selected] });
      qc.invalidateQueries({ queryKey: ["admin-tickets"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reopen = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error("No ticket selected");
      const { error } = await supabase.from("tickets").update({ status: "open" }).eq("id", selected);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Ticket reopened");
      qc.invalidateQueries({ queryKey: ["admin-tickets"] });
    },
  });

  const current = tickets.find((t: any) => t.id === selected);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Support tickets</h2>
        <p className="text-sm text-muted-foreground">Reply to user tickets and resolve them when done.</p>
      </div>

      <div className="flex gap-1">
        {(["open", "closed", "all"] as const).map((f) => (
          <Button key={f} size="sm" variant={filter === f ? "default" : "ghost"} onClick={() => setFilter(f)} className="capitalize">
            {f}
          </Button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-4">
        <div className="glass rounded-2xl p-3 max-h-[70vh] overflow-y-auto">
          {tickets.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No tickets</div>
          ) : (
            <div className="space-y-1">
              {tickets.map((t: any) => (
                <button
                  key={t.id}
                  onClick={() => setSelected(t.id)}
                  className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors ${
                    selected === t.id ? "bg-primary/15 border border-primary/30" : "hover:bg-white/5 border border-transparent"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium truncate">{t.subject}</div>
                    <Badge variant={t.status === "open" ? "default" : "secondary"} className="text-[10px] capitalize">
                      {t.status}
                    </Badge>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                    {t.profile?.full_name || t.profile?.email || "—"}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{fmtDate(t.updated_at)}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="glass rounded-2xl p-4 min-h-[400px] flex flex-col">
          {!current ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              <div className="text-center">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                Select a ticket to view the conversation
              </div>
            </div>
          ) : (
            <>
              <div className="border-b border-white/10 pb-3 mb-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold">{current.subject}</h3>
                  <Badge variant={current.status === "open" ? "default" : "secondary"} className="capitalize">
                    {current.status}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  From: {current.profile?.full_name || "—"} · {current.profile?.email}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto space-y-3 max-h-[400px]">
                {messages.map((m: any) => (
                  <div key={m.id} className={`flex ${m.is_admin ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[80%] rounded-lg px-3 py-2 ${
                        m.is_admin ? "bg-primary/20 border border-primary/30" : "bg-white/5"
                      }`}
                    >
                      <div className="text-[10px] text-muted-foreground mb-1 flex items-center gap-1">
                        {m.is_admin && <ShieldCheck className="h-3 w-3 text-primary" />}
                        {m.is_admin ? "You (admin)" : "User"} · {fmtDate(m.created_at)}
                      </div>
                      <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-3 mt-3 space-y-2">
                <Textarea
                  rows={3}
                  placeholder="Write a reply…"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                />
                <div className="flex justify-between gap-2">
                  {current.status === "closed" ? (
                    <Button size="sm" variant="outline" onClick={() => reopen.mutate()}>Reopen ticket</Button>
                  ) : <span />}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!reply.trim() || send.isPending}
                      onClick={() => send.mutate({ body: reply, close: true })}
                    >
                      Reply & close
                    </Button>
                    <Button
                      size="sm"
                      disabled={!reply.trim() || send.isPending}
                      onClick={() => send.mutate({ body: reply, close: false })}
                    >
                      {send.isPending ? "Sending…" : "Send reply"}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}